import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, FormControl, InputLabel, Select, MenuItem,
  TextField, Chip, IconButton, Tooltip, CircularProgress,
  Collapse, Button
} from '@mui/material';
import {
  FilterListOutlined, ClearOutlined, ExpandMoreOutlined, ExpandLessOutlined
} from '@mui/icons-material';
import API from '../utils/api';

const SX_SELECT = { minWidth: 150 };
const SX_CHIP   = {
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '0.68rem',
  bgcolor: 'rgba(0,191,165,0.1)',
  color: 'primary.main',
  border: '1px solid rgba(0,191,165,0.25)',
};

export const EMPTY_FILTERS = {
  region:   'all',
  facility: 'all',
  telecom:  'all',
  groupId:  'all',
  dateFrom: '',
  dateTo:   '',
};

export default function FilterBar({ filters, onChange, showGroup = true, showDate = true }) {
  const [options, setOptions]   = useState({ regions: [], facilities: [], telecoms: [], groups: [], dateRange: {} });
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState(false);

  // Load filter options; re-fetch facilities when region changes
  const loadOptions = useCallback(async (region) => {
    try {
      const res = await API.get('/filters/options', { params: { region } });
      setOptions(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadOptions(filters.region); }, [filters.region, loadOptions]);

  const set = (key, value) => onChange({ ...filters, [key]: value,
    // reset facility when region changes
    ...(key === 'region' ? { facility: 'all' } : {})
  });

  const activeCount = Object.entries(filters)
    .filter(([, v]) => v && v !== 'all' && v !== '').length;

  const clearAll = () => onChange(EMPTY_FILTERS);

  if (loading) return (
    <Box display="flex" alignItems="center" gap={1} mb={2}>
      <CircularProgress size={16} />
    </Box>
  );

  return (
    <Box mb={2.5}>
      {/* Filter toggle bar */}
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Button
          size="small"
          startIcon={<FilterListOutlined />}
          endIcon={expanded ? <ExpandLessOutlined /> : <ExpandMoreOutlined />}
          onClick={() => setExpanded(!expanded)}
          sx={{
            fontFamily: '"IBM Plex Mono", monospace', fontSize: '0.72rem',
            color: activeCount > 0 ? 'primary.main' : 'text.secondary',
            borderColor: activeCount > 0 ? 'primary.main' : 'rgba(255,255,255,0.12)',
            border: '1px solid', borderRadius: '8px', px: 1.5, py: 0.5,
            textTransform: 'none',
          }}
        >
          Filters {activeCount > 0 ? `(${activeCount} active)` : ''}
        </Button>

        {/* Active filter chips */}
        {filters.region   !== 'all'  && <Chip size="small" label={`Region: ${filters.region}`}   onDelete={() => set('region','all')}   sx={SX_CHIP} />}
        {filters.facility !== 'all'  && <Chip size="small" label={`Facility: ${filters.facility}`} onDelete={() => set('facility','all')} sx={SX_CHIP} />}
        {filters.telecom  !== 'all'  && <Chip size="small" label={`Telecom: ${filters.telecom}`}  onDelete={() => set('telecom','all')}  sx={SX_CHIP} />}
        {filters.groupId  !== 'all'  && <Chip size="small" label={`Group: ${options.groups.find(g=>g.id===parseInt(filters.groupId))?.name || filters.groupId}`} onDelete={() => set('groupId','all')} sx={SX_CHIP} />}
        {filters.dateFrom            && <Chip size="small" label={`From: ${filters.dateFrom}`}    onDelete={() => set('dateFrom','')}    sx={SX_CHIP} />}
        {filters.dateTo              && <Chip size="small" label={`To: ${filters.dateTo}`}        onDelete={() => set('dateTo','')}      sx={SX_CHIP} />}

        {activeCount > 0 && (
          <Tooltip title="Clear all filters">
            <IconButton size="small" onClick={clearAll} sx={{ color: 'error.main' }}>
              <ClearOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Expanded filter controls */}
      <Collapse in={expanded}>
        <Box
          display="flex" flexWrap="wrap" gap={1.5} mt={1.5} p={2}
          sx={{ bgcolor: 'rgba(0,191,165,0.04)', borderRadius: 2, border: '1px solid rgba(0,191,165,0.1)' }}
        >
          {/* Region */}
          <FormControl size="small" sx={SX_SELECT}>
            <InputLabel>Region</InputLabel>
            <Select value={filters.region} label="Region" onChange={e => set('region', e.target.value)}>
              <MenuItem value="all">All Regions</MenuItem>
              {options.regions.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>

          {/* Facility — scoped to selected region */}
          <FormControl size="small" sx={{ minWidth: 210 }}>
            <InputLabel>Facility</InputLabel>
            <Select value={filters.facility} label="Facility" onChange={e => set('facility', e.target.value)}>
              <MenuItem value="all">All Facilities</MenuItem>
              {options.facilities
                .filter(f => filters.region === 'all' || f.region === filters.region)
                .map(f => <MenuItem key={f.name} value={f.name}>{f.name}</MenuItem>)
              }
            </Select>
          </FormControl>

          {/* Telecom */}
          <FormControl size="small" sx={SX_SELECT}>
            <InputLabel>Telecom</InputLabel>
            <Select value={filters.telecom} label="Telecom" onChange={e => set('telecom', e.target.value)}>
              <MenuItem value="all">All Telecoms</MenuItem>
              {options.telecoms.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>

          {/* Disease / Group */}
          {showGroup && (
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel>Disease Group</InputLabel>
              <Select value={filters.groupId} label="Disease Group" onChange={e => set('groupId', e.target.value)}>
                <MenuItem value="all">All Groups</MenuItem>
                {options.groups.map(g => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.name} {g.count ? `(${g.count})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Date range */}
          {showDate && (
            <>
              <TextField
                size="small" type="date" label="Enrolled From"
                value={filters.dateFrom}
                onChange={e => set('dateFrom', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: options.dateRange?.min, max: filters.dateTo || options.dateRange?.max }}
                sx={{ minWidth: 160 }}
              />
              <TextField
                size="small" type="date" label="Enrolled To"
                value={filters.dateTo}
                onChange={e => set('dateTo', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: filters.dateFrom || options.dateRange?.min, max: options.dateRange?.max }}
                sx={{ minWidth: 160 }}
              />
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
